import React, { useContext, useCallback, useMemo } from 'react'
import { useForm, FormProvider } from 'react-hook-form'
import Modal from 'react-modal'
import _ from 'lodash'
import { useDispatch, useSelector } from 'react-redux'

// design & custom components
import Tabs, { TabPanel } from 'Design/components/Tabs'

// form validation
import { yupResolver } from '@hookform/resolvers/yup'
import mlSearchFormValidationSchema from './ml-search-form-validation-schema'

// assets
import CloseIcon from 'Icons/close.svg'

// contexts & store
import { setFlowHistory, updateAction, updateBotResponseSettings } from 'Actions/flow'
import { StepContext, useFlowContext } from '../../../contexts'

import {
  getEscalateStep,
  getMlActionPayload,
  getDefaultBotSettings,
  cleanKBSections,
  convertToSectionArray
} from './bot-response-setting-helper'
import KnowledgeBaseConfigTab from './KnowledgeBaseConfigTab'
import MessagesConfigTab from './MessagesConfigTab'
import { getAccountDetails } from 'Selectors/account'
import PageLoader from 'Layout/PageLoader'

const MLSearch = (props) => {
  // props
  const {
    isOpen,
    onCloseModal,
    actionId,
    isNewUI,
    setMlConfig,
    clearStepSelection,
    mode = 'edit',
    allKBSections,
    showLoader
  } = props
  // context & store
  const dispatch = useDispatch()
  const { steps } = useFlowContext()
  const { step, stepId, row } = useContext(StepContext)

  const accountData = useSelector(getAccountDetails)

  const settings = _.get(step, 'settings', {})
  const action = _.get(settings, `${actionId}`, {})
  const escalateStep = getEscalateStep({ ...steps })
  const allSections =
    allKBSections?.map((kb) => ({
      kb_id: kb.id,
      sections: kb.sections.map((section) => section.id)
    })) || []
  const botSettingConfig = getDefaultBotSettings({ ...action }, escalateStep, stepId, allSections, accountData)
  // form
  const form = useForm({
    defaultValues: { ...botSettingConfig },
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(mlSearchFormValidationSchema),
    context: {
      showEmptyResponseMessage: !!accountData.settings?.ai_predictions
    }
  })
  const {
    watch,
    getValues,
    handleSubmit,
    formState: { errors }
  } = form
  const ackMsgErrors = _.get(errors, 'acknowledgementMessages')
  const ackMsg = watch('acknowledgementMessages', getValues('acknowledgementMessages'))

  const feedbackMsgErrors = _.get(errors, 'feedbackMessages')
  const feedbackMsg = watch('feedbackMessages', getValues('feedbackMessages'))
  const selectedSections = watch('selectedSections', getValues('selectedSections'))

  const dynamicFieldErrors = useMemo(() => {
    const map = {}
    if (ackMsgErrors && ackMsgErrors.length > 0) {
      ackMsgErrors.forEach(({ message }, index) => {
        if (ackMsg[index] && ackMsg[index].message && _.trim(ackMsg[index].message) !== '') {
          return
        }
        map[`acknowledgementMessages.${index}`] = message
      })
    }
    if (feedbackMsgErrors && feedbackMsgErrors.length > 0) {
      feedbackMsgErrors.forEach(({ message }, index) => {
        if (feedbackMsg[index] && feedbackMsg[index].message && _.trim(feedbackMsg[index].message) !== '') {
          return
        }
        map[`feedbackMessages.${index}`] = message
      })
    }
    return map
  }, [ackMsgErrors, feedbackMsgErrors, ackMsg, feedbackMsg])

  const sectionArray = convertToSectionArray(selectedSections)
  const cleanedSections = cleanKBSections(sectionArray, allSections)

  const updateMlConfigForm = useCallback(
    (formData) => {
      setMlConfig({
        question_placeholder: formData.question_placeholder,
        acknowledgementMessages: formData.acknowledgementMessages.map((ack) => ack.message),
        feedbackMessages: formData.feedbackMessages.map((feed) => feed.message),
        helpfulLabel: formData.helpfulLabel,
        helpfulAction: { ...formData.helpfulAction },
        notHelpfulLabel: formData.notHelpfulLabel,
        notHelpfulAction: { ...formData.notHelpfulAction },
        noResultLabel: formData.noResultLabel,
        noResultAction: { ...formData.noResultAction },
        selectedSections: cleanedSections,
        selectedTags: [...formData.selectedTags],
        enableKBFilter: formData.enableKBFilter,
        enableTagsFilter: formData.enableTagsFilter
      })
    },
    [setMlConfig, cleanedSections]
  )

  const handleFormSubmit = useCallback(
    (data) => {
      if (!_.isEmpty(errors) || !_.isEmpty(dynamicFieldErrors)) {
        return
      }

      updateMlConfigForm({ ...data })
      const payload = getMlActionPayload({ ...data }, undefined, stepId, actionId, accountData)
      const botResponseSettings = _.omit({ ...payload.setting }, [
        'type',
        'collect_feedback',
        'enable_kb_filter',
        'enable_tags_filter',
        'selected_sections',
        'selected_tags'
      ])
      if (mode === 'edit') {
        dispatch(
          updateAction({
            ...payload
          })
        )

        dispatch(updateBotResponseSettings(botResponseSettings))
      }
      clearStepSelection()
      dispatch(setFlowHistory({ goToStep: stepId, row: row - 1 }))
      onCloseModal()
    },
    [
      actionId,
      clearStepSelection,
      dispatch,
      dynamicFieldErrors,
      errors,
      mode,
      onCloseModal,
      row,
      stepId,
      updateMlConfigForm,
      accountData
    ]
  )

  const onCancelMlEdit = () => {
    onCloseModal()
  }

  return (
    <div>
      <Modal
        isOpen={isOpen}
        contentLabel='Edit Bot Modal'
        ariaHideApp={false}
        className={isNewUI ? 'ml-search-modal new-design' : 'ml-search-modal'}
      >
        {showLoader && <PageLoader />}
        {!showLoader && (
          <FormProvider {...form}>
            <form className='ml-search-form' onSubmit={handleSubmit(handleFormSubmit)}>
              <div className='modal-header'>
                Customize Bot Response
                <div className='modal-close' onClick={() => onCancelMlEdit()}>
                  <CloseIcon height='18' width='18' />
                </div>
              </div>
              <div className='modal-body'>
                <Tabs>
                  <TabPanel name='Messages'>
                    <MessagesConfigTab isNewUI={isNewUI} dynamicFieldErrors={dynamicFieldErrors} />
                  </TabPanel>
                  <TabPanel name='Knowledge Base'>
                    <KnowledgeBaseConfigTab allKBSections={allKBSections} allSections={allSections} />
                  </TabPanel>
                </Tabs>
              </div>
              <div className='modal-footer'>
                <div className='action-group'>
                  <button className='button-primary save' type='submit'>
                    Save
                  </button>
                  <button className='button-link cancel' onClick={() => onCancelMlEdit()}>
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </FormProvider>
        )}
      </Modal>
    </div>
  )
}

export default MLSearch
